import React, {Component} from 'react';
import classnames from 'classnames';
import {SVGIcon} from '@homeaway/react-svg';
import {evaluateExperimentAndLog} from '@homeaway/abacus-client-utils';

import {getSuggestionDetails} from '../functions/suggestiondetails';
import {HA_TYPEAHEAD_TWO_LINE_DISPLAY} from '../experiments';
import {isListingIdSearch} from '../functions/destinationHelper';
import {Thumbnail} from './thumbnail';

export class ResultRow extends Component {
    isLoggedTwoLineExperiment = false;

    skipFilters = (suggestion) =>
        this.props.skipFilters ||
        suggestion.recentlyviewed ||
        !suggestion.subSuggestions;

    getIconDom = (result, suggestionsDetails) => {
        const {showResultIcons} = this.props;
        const iconDOM = [];
        if (showResultIcons) {
            iconDOM.push(
                <SVGIcon
                    key={`result__icon-${result.uuid}`}
                    markup={suggestionsDetails.icon}
                    className="result__icon"
                />
            );
        }
        return iconDOM;
    };

    getTwoLineExperimentElementTitleDom = (term, reactKey) => {
        let [firstLine, secondLine] = term.split(',');
        secondLine = secondLine ? term.slice(firstLine.length + 1) : null;
        return (
            <span key={reactKey}>
                <span
                    className={classnames('result__char', {
                        highlighted: true
                    })}
                    key={`${reactKey}-${firstLine}`}
                >
                    {firstLine}
                </span>
                {secondLine && (
                    <>
                        <br />
                        <span
                            className={classnames('result__char')}
                            key={`${reactKey}-${secondLine}`}
                        >
                            {secondLine}
                        </span>
                    </>
                )}
            </span>
        );
    };
    getHighlightedTitleDOM = (highlightedTerm, highlightMode, reactKey) => {
        const titleDOM = [];
        for (let i = 0; i < highlightedTerm.length; i++) {
            const highlighted = highlightedTerm[i].highlighted;
            const charClassName = classnames('result__char', {
                highlighted:
                    (highlighted && highlightMode === 'match') ||
                    (!highlighted && highlightMode === 'non-match')
            });
            titleDOM.push(
                <span className={charClassName} key={`${reactKey}-${i}`}>
                    {highlightedTerm[i].value}
                </span>
            );
        }
        return titleDOM;
    };
    checkTwoLineExperiment = () => {
        if (
            window.__ABACUS__ &&
            window.__ABACUS__[HA_TYPEAHEAD_TWO_LINE_DISPLAY] &&
            !this.isLoggedTwoLineExperiment
        ) {
            evaluateExperimentAndLog(HA_TYPEAHEAD_TWO_LINE_DISPLAY);
            this.isLoggedTwoLineExperiment = true;
        }
    };

    getTitleDom = (highlightedTerm, reactKey, term, isLIDSearch = false) => {
        let {highlightMode} = this.props;
        const titleDOM = [];
        this.checkTwoLineExperiment();
        if (
            !isLIDSearch &&
            window.__ABACUS__ &&
            window.__ABACUS__[HA_TYPEAHEAD_TWO_LINE_DISPLAY] &&
            window.__ABACUS__[HA_TYPEAHEAD_TWO_LINE_DISPLAY].value > 0
        ) {
            titleDOM.push(
                this.getTwoLineExperimentElementTitleDom(term, reactKey)
            );
        } else {
            this.getHighlightedTitleDOM(
                highlightedTerm,
                highlightMode,
                reactKey
            ).forEach((item) => {
                titleDOM.push(item);
            });
        }
        titleDOM.push('');
        return titleDOM;
    };

    addRecentlyViewed = (dom, result, reactKey) => {
        const {messages} = this.props;
        if (result.recentlyviewed) {
            dom.push(
                <span
                    className="recentlyviewed micro"
                    key={`${reactKey}-recentlyviewed`}
                >
                    {messages.recentlyviewed}
                </span>
            );
        }
    };

    addLabel = (dom, result, suggestionsDetails) => {
        const {showResultLabels, messages, poweredByGoogle} = this.props;
        if (
            suggestionsDetails.label &&
            showResultLabels &&
            (!poweredByGoogle || result.recentlyviewed)
        ) {
            dom.push(
                <span
                    key={`result__label-${result.uuid}`}
                    className="result__label hidden-xs small"
                >
                    {messages[suggestionsDetails.label]}
                </span>
            );
        }
    };

    addFilter = (dom, result, subResult) => {
        const {highlightMode} = this.props;
        if (subResult && subResult.filter && subResult.filter.name) {
            dom.push(
                <span
                    className={classnames('result__mark result__name-inline', {
                        highlighted: highlightMode === 'non-match'
                    })}
                    key={`result-inline-filter-${result.uuid}`}
                >
                    {subResult.filter.name}
                </span>
            );
        }
    };

    renderListingContent = (
        shouldDisplayListingWithHeadline,
        resultCharsClassName,
        result,
        titleDOM
    ) => {
        let {
            listingProps: {headline = null, baseLocation = null, unitId = null}
        } = result;
        if (!shouldDisplayListingWithHeadline || headline === '') {
            headline = <br />;
        }
        return (
            <div>
                <div className={resultCharsClassName}>{headline}</div>
                <div className={resultCharsClassName}>
                    {titleDOM}
                    {unitId && (
                        <span className={classnames('listing__location')}>
                            {unitId}
                        </span>
                    )}
                    {baseLocation && (
                        <span className={classnames('listing__location')}>
                            {baseLocation}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    /* eslint-disable complexity */
    displayTerm = (result, subResult) => {
        const {truncateResultTerms} = this.props;
        const dom = [];
        const reactKey = `searchresults-${result.uuid}-span`;
        const suggestionsDetails = getSuggestionDetails(result.type);
        const iconDOM = this.getIconDom(result, suggestionsDetails);
        const {highlightedTerm, term} = result;
        const isLIDSearch = isListingIdSearch(result);
        let titleDOM = this.getTitleDom(
            highlightedTerm,
            reactKey,
            term,
            isLIDSearch
        );
        this.addFilter(dom, result, subResult);
        this.addRecentlyViewed(dom, result, reactKey);
        // show type labels for destinations except Google failover destinations
        this.addLabel(dom, result, suggestionsDetails);
        const resultCharsClassName = classnames('result__chars', {
            'result__chars--truncate-term': truncateResultTerms
        });

        const listingIcon =
            isLIDSearch && result && result.listingProps
                ? [
                      <span
                          className={classnames('result__listing-thumbnail')}
                          key={`result-thumbnail-${reactKey}`}
                      >
                          <Thumbnail
                              thumbnailUrl={result.listingProps.thumbnailUrl}
                          />
                      </span>
                  ]
                : [];
        const shouldDisplayListingWithHeadline =
            listingIcon.length && result.listingProps.headline;
        return [
            ...iconDOM,
            ...listingIcon,
            <span
                className={resultCharsClassName}
                key={`result-chars-${reactKey}`}
            >
                {!isLIDSearch
                    ? titleDOM
                    : this.renderListingContent(
                          shouldDisplayListingWithHeadline,
                          resultCharsClassName,
                          result,
                          titleDOM
                      )}
            </span>,
            ...dom
        ];
    };

    renderResultRow = (attributes, selected, style, title) => {
        let attr = attributes;
        if (
            window.__ABACUS__ &&
            window.__ABACUS__[HA_TYPEAHEAD_TWO_LINE_DISPLAY] &&
            window.__ABACUS__[HA_TYPEAHEAD_TWO_LINE_DISPLAY].value > 0
        ) {
            let className = attributes.className || '';
            className = classnames(className, 'result--two-line-display');
            attr = {
                ...attributes,
                className
            };
        }
        return (
            <div tabIndex="-1" {...attr}>
                <div className="result__bg bg-brand" />

                <div
                    role="option"
                    aria-selected={selected}
                    tabIndex="-1"
                    className="result__content"
                    style={style}
                >
                    {title}
                </div>
            </div>
        );
    };

    showTrendingHeading = (inputHasIcon) => {
        const {messages} = this.props;

        return (
            <div
                className={classnames(
                    'trending-destinations-heading',
                    'small',
                    {
                        'heading__has-input-icon': inputHasIcon
                    }
                )}
            >
                {messages.trendingdestinations}
            </div>
        );
    };

    renderSubSuggestions = (
        result,
        suggestionIndex,
        attributes,
        classNames,
        style
    ) => {
        const {
            filterType,
            selectDestinationSubIndex,
            showResultIcons,
            inputHasIcon
        } = this.props;

        return result.subSuggestions.map((subSuggestion, index) => {
            let filterTitleDOM = null;
            const selected = index === selectDestinationSubIndex;

            const subSuggestionAttributes = {
                ...attributes,
                ['className']: classnames({selected}, classNames),
                ['data-sub-suggestion-key']: index,
                ['key']: `searchresults-div-${suggestionIndex}-${index}-sub-result`
            };
            if (filterType === 2) {
                // inline mark with result
                subSuggestionAttributes.className = classnames(
                    subSuggestionAttributes.className,
                    this.getResultClassesForIcons({
                        showResultIcons,
                        inputHasIcon
                    })
                );
                filterTitleDOM = this.displayTerm(result, subSuggestion);
            } else {
                // below with arrow or mark
                subSuggestionAttributes.className = classnames(
                    subSuggestionAttributes.className,
                    this.getResultClassesForIcons({
                        showResultIcons,
                        inputHasIcon,
                        subResult: true
                    })
                );
                filterTitleDOM = this.getFilterTitle(subSuggestion);
            }
            if (!filterTitleDOM) {
                return null;
            }
            return this.renderResultRow(
                subSuggestionAttributes,
                selected,
                style,
                filterTitleDOM
            );
        });
    };

    getFilterTitle = (subSuggestion) => {
        const {filterType, showResultIcons, highlightMode} = this.props;
        const filterName = subSuggestion.filter && subSuggestion.filter.name;

        if (!filterName) {
            return null;
        }

        return (
            <React.Fragment>
                {filterType === 1 && (
                    <span
                        className={classnames('result__name', 'result__mark', {
                            'result__name-intended': !showResultIcons,
                            'result__name-intended-icon': showResultIcons,
                            highlighted: highlightMode === 'non-match'
                        })}
                    >
                        {filterName}
                    </span>
                )}
                {filterType === 3 && (
                    <React.Fragment>
                        <span
                            className={classnames({
                                'SVGIcon--arrow': !showResultIcons,
                                'SVGIcon--arrow-icon': showResultIcons
                            })}
                        >
                            <SVGIcon id="ARROW_DOWN_RIGHT__16" />
                        </span>
                        <span
                            className={classnames('result__name', {
                                highlighted: highlightMode === 'non-match'
                            })}
                        >
                            {filterName}
                        </span>
                    </React.Fragment>
                )}
            </React.Fragment>
        );
    };

    getResultClassesForIcons({
        showResultIcons,
        inputHasIcon,
        subResult,
        isLIDSearch = false
    }) {
        const suffix = subResult ? '-is-sub-result' : '';
        const toPrefix = (bool) => (bool ? 'has' : 'no');

        return classnames({
            [`result--${toPrefix(showResultIcons)}-icon-${toPrefix(
                inputHasIcon
            )}-input-icon${suffix}`]: !isLIDSearch
        });
    }

    render() {
        const {
            index,
            hasSelectedIndex,
            selectDestinationSubIndex,
            isMobile,
            last,
            result,
            selected,
            showResultIcons,
            inputHasIcon,
            hasTrendingHeading
        } = this.props;

        let filtersDOM = null;
        let classNames = {
            result: true,
            in: true,
            'has-selected-index': hasSelectedIndex,
            'recent-search__result': result.recentlyviewed,
            last,
            mobile: isMobile,
            desktop: !isMobile
        };
        const style = {};

        const suggestionDOM = this.displayTerm(result);

        const attributes = {
            ['data-suggestion-key']: index,
            ['key']: `searchresults-div-${index}-result`
        };

        const isLIDSearch = isListingIdSearch(result);
        const resultAttributes = {
            ...attributes,
            ['className']: classnames(
                {selected: selected && selectDestinationSubIndex === -1},
                classNames,
                this.getResultClassesForIcons({
                    showResultIcons,
                    inputHasIcon,
                    isLIDSearch
                })
            )
        };
        const skipFilters = this.skipFilters(result);
        if (!skipFilters) {
            filtersDOM = this.renderSubSuggestions(
                result,
                index,
                attributes,
                classNames,
                style
            );
        }
        let trendingHeading = null;
        if (hasTrendingHeading) {
            trendingHeading = this.showTrendingHeading(inputHasIcon);
        }

        return (
            <React.Fragment>
                {trendingHeading}
                {this.renderResultRow(
                    resultAttributes,
                    selected,
                    style,
                    suggestionDOM
                )}
                {filtersDOM}
            </React.Fragment>
        );
    }
}
